import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ACTIVITY_EVENTS,
  ACTIVITY_WRITE_THROTTLE_MS,
  CHECK_INTERVAL_MS,
  IDLE_TIMEOUT_MS,
  LAST_ACTIVITY_STORAGE_KEY,
  WARNING_BEFORE_MS,
  isRememberMeActive,
  isSessionStale,
  readLastActivity,
  writeLastActivity,
} from '@/helpers/sessionTimeout';

/**
 * סופר חוסר פעילות ומודיע כשהסשן צריך להסתיים (HAT-1092).
 *
 * חותמת הפעילות יושבת ב-localStorage ולא ב-state, משתי סיבות: היא שורדת
 * סגירת דפדפן (וזה בדיוק המקרה שדווח - חוזרים אחרי הרבה זמן), והיא משותפת
 * לכל הלשוניות, כך שעבודה באחת לא מנתקת את השנייה.
 *
 * נוכחות נרשמת לא רק מלחיצה/מקלדת: גלילה (כולל במכולה פנימית, ב-capture),
 * גלגלת, מגע, וחזרה לחלון/לשונית (focus + visibilitychange). בלי זה קריאה
 * או מעבר למייל וחזרה נראו כחוסר פעילות וניתקו באמצע העבודה.
 */

interface UseSessionTimeoutArgs {
  /** נספר רק כשיש משתמש מחובר. עמודים ציבוריים לא מושפעים. */
  enabled: boolean;
  onTimeout: () => void;
}

type IdleCheckStatus = 'ok' | 'warning' | 'timeout';

const ACTIVITY_LISTENER_OPTIONS: AddEventListenerOptions = { passive: true, capture: true };

export const useSessionTimeout = ({ enabled, onTimeout }: UseSessionTimeoutArgs) => {
  /** null = אין אזהרה. מספר = הזמן שנותר עד הניתוק, לתצוגת ספירה לאחור. */
  const [msUntilLogout, setMsUntilLogout] = useState<number | null>(null);
  const lastWriteRef = useRef(0);
  /**
   * בזמן האזהרה מקפיאים את איתור הפעילות: עכבר שנגרר על שולחן ריק, או
   * סקריפט שמגלגל את העמוד, לא אמורים להאריך סשן שאיש לא נמצא מולו.
   * רק לחיצה מפורשת על "להישאר מחובר" מאריכה.
   */
  const isWarningRef = useRef(false);
  const onTimeoutRef = useRef(onTimeout);

  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  const registerActivity = useCallback((force = false) => {
    // חותמת שכבר פגה לא מתחדשת מגלילה/פוקוס של טעינת העמוד — אחרת סשן
    // ישן "קם לתחייה" לפני שבכלל הספקנו לנתק.
    if (!force && isSessionStale()) return;
    const now = Date.now();
    if (!force && now - lastWriteRef.current < ACTIVITY_WRITE_THROTTLE_MS) return;
    lastWriteRef.current = now;
    writeLastActivity(now);
  }, []);

  /** "להישאר מחובר" - מאפס את הספירה וסוגר את האזהרה. */
  const extendSession = useCallback(() => {
    isWarningRef.current = false;
    setMsUntilLogout(null);
    registerActivity(true);
  }, [registerActivity]);

  useEffect(() => {
    if (!enabled) {
      isWarningRef.current = false;
      setMsUntilLogout(null);
      return;
    }

    // סשן שנפתח עכשיו, או שהיה פתוח עוד לפני התיקון הזה ואין לו חותמת,
    // מתחיל למנות מכאן - אין לנו ראיה שהוא ישן, ולא מנתקים על סמך ניחוש.
    if (readLastActivity() === null) writeLastActivity();

    const check = (): IdleCheckStatus => {
      // "זכור אותי" — דילוג על ניתוק idle כל עוד החלון של שבוע פעיל.
      if (isRememberMeActive()) {
        isWarningRef.current = false;
        setMsUntilLogout(null);
        return 'ok';
      }

      const lastActivity = readLastActivity() ?? Date.now();
      const idleFor = Date.now() - lastActivity;

      if (idleFor >= IDLE_TIMEOUT_MS) {
        isWarningRef.current = false;
        setMsUntilLogout(null);
        onTimeoutRef.current();
        return 'timeout';
      }

      if (idleFor >= IDLE_TIMEOUT_MS - WARNING_BEFORE_MS) {
        isWarningRef.current = true;
        setMsUntilLogout(IDLE_TIMEOUT_MS - idleFor);
        return 'warning';
      }

      if (isWarningRef.current) {
        // פעילות בלשונית אחרת החזירה אותנו אחורה
        isWarningRef.current = false;
        setMsUntilLogout(null);
      }
      return 'ok';
    };

    const handleActivity = () => {
      if (isWarningRef.current) return;
      // גלילה/גלגלת בטעינה לא יחיו סשן שכבר פג, ורק אחרי שהוכחנו שהחותמת בתוקף נרשום נוכחות.
      if (check() !== 'ok') return;
      registerActivity();
    };

    /**
     * חזרה לחלון או ללשונית היא שימוש אמיתי. קודם בודקים אם כבר עבר הזמן
     * (ואז מנתקים), ורק אם הסשן עדיין בתוקף ולא באזהרה — מרעננים את החותמת.
     * בלי זה מעבר למייל/אקסל וחזרה נראה כחוסר פעילות.
     */
    const handlePresence = () => {
      if (check() === 'ok') registerActivity();
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') handlePresence();
    };

    // פעילות בלשונית אחרת מאריכה גם כאן - אותו משתמש, אותו סשן.
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== LAST_ACTIVITY_STORAGE_KEY || !event.newValue) return;
      check();
    };

    // קודם בודקים את החותמת ששוחזרה, ורק אז מאזינים. אחרת scroll/focus/wheel
    // של הציור הראשון דורסים חותמת ישנה ב-Date.now() והסשן נראה רענן.
    if (check() === 'timeout') return;

    ACTIVITY_EVENTS.forEach((event) => document.addEventListener(event, handleActivity, ACTIVITY_LISTENER_OPTIONS));
    window.addEventListener('focus', handlePresence);
    window.addEventListener('storage', handleStorage);
    document.addEventListener('visibilitychange', handleVisibility);
    const interval = window.setInterval(check, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((event) => document.removeEventListener(event, handleActivity, ACTIVITY_LISTENER_OPTIONS));
      window.removeEventListener('focus', handlePresence);
      window.removeEventListener('storage', handleStorage);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.clearInterval(interval);
    };
  }, [enabled, registerActivity]);

  return { msUntilLogout, extendSession };
};
